// The measure's own price book on the device (hig_jobprice, #413): a job price
// typed for a price-book paint, or a paint on this job only. A row saves and
// syncs like the job's other records; every option on the measure is priced
// from it (@higgins/m2q-pricing job-prices.ts).
import { uuidV5 } from '@higgins/condition-library';
import { JOB_PRICE_KIND, JOB_PRICE_TABLE, jobPricesOf, oneOffPaintKey, oneOffPaints, paintLabel, type JobPrice, type PaintChoice, type PricingRates } from '@higgins/m2q-pricing';
import { saveRecord } from './device-db';
import { saveFields } from './options';
import { syncNow } from './outbox';
import type { DeviceRecord } from './device-store';
import type { HireItemFromApi } from './types';

/** Namespace for job price ids, so the same product on the same measure is one row on every device. */
const JOB_PRICE_NAMESPACE = '6f3b2c1e-8a4d-4e5f-9b7a-2d1c0e9f8a71';

/** A book item's row id on a measure: the same item on the same measure is one row on every device. */
const bookRowId = (kind: 'paint' | 'hire', measureId: string, itemId: string) => uuidV5(`${kind}/${measureId.toLowerCase()}/${itemId.toLowerCase()}`, JOB_PRICE_NAMESPACE);

async function saveNew(measure: DeviceRecord, id: string, kind: string, fields: DeviceRecord['fields']): Promise<void> {
  await saveRecord({ id, table: JOB_PRICE_TABLE, jobId: measure.jobId, parentId: measure.id, etag: null, fields: { hig_kind: kind, ...fields } });
  void syncNow();
}

/** Write a book item's job price: into its existing row, or a new one with what identifies it. */
async function upsertBookRow(measure: DeviceRecord, records: readonly DeviceRecord[], id: string, kind: string, identity: DeviceRecord['fields'], fields: DeviceRecord['fields']): Promise<void> {
  const existing = records.find((r) => r.id === id);
  if (existing) await saveFields(existing, fields);
  else await saveNew(measure, id, kind, { ...identity, ...fields });
}

/**
 * Type (or clear, with null) the job's price for a price-book paint. It prices every option on the
 * measure; clearing it leaves the row with no price, so the book price stands again. The book price it
 * replaces is recorded by the server when the row is made, and kept through later edits.
 */
async function setPaintJobPrice(measure: DeviceRecord, records: readonly DeviceRecord[], paint: { productId: string; name: string }, cost: number | null): Promise<void> {
  await upsertBookRow(measure, records, bookRowId('paint', measure.id, paint.productId), JOB_PRICE_KIND.paint, { hig_name: paint.name, hig_paintproductid: paint.productId }, { hig_costperlitre: cost });
}

/** Add a paint on this job only (name + $/L ex GST); returns its row id, which the option's paint list keys it by. */
async function addOneOffPaint(measure: DeviceRecord, name: string, cost: number): Promise<string> {
  const id = crypto.randomUUID();
  await saveNew(measure, id, JOB_PRICE_KIND.paint, { hig_name: name, hig_costperlitre: cost });
  return id;
}

/** Rename or re-price a one-off paint; every option using it follows. */
const updateOneOffPaint = (row: DeviceRecord, change: { hig_name?: string; hig_costperlitre?: number | null }): Promise<void> => saveFields(row, change);

/** What the desk can do to the job's price book from an option's paint list. */
export interface JobPaintActions {
  /** The job's one-off paints, so another option can use one. */
  oneOffs: JobPrice[];
  /** A job price for a price-book paint; null clears it (the book price again). */
  onJobPrice: (paint: PaintChoice, cost: number | null) => void;
  onAddOneOff: (name: string, cost: number) => void;
  onOneOff: (jobPriceId: string, change: { hig_name?: string; hig_costperlitre?: number | null }) => void;
}

/** The paint list's job-price actions for one option on `measure`, priced on `date`. */
export function jobPaintActions(measure: DeviceRecord, records: readonly DeviceRecord[], onAddPaint: (paint: PaintChoice) => unknown): JobPaintActions {
  const rows = new Map(records.filter((r) => r.table === JOB_PRICE_TABLE).map((r) => [r.id, r]));
  return {
    oneOffs: oneOffPaints(jobPricesOf(records, measure.id)),
    onJobPrice: (paint, cost) => {
      if (paint.productId) void setPaintJobPrice(measure, records, { productId: paint.productId, name: paint.name }, cost);
    },
    onAddOneOff: (name, cost) => {
      void addOneOffPaint(measure, name, cost).then((id) => onAddPaint({ key: oneOffPaintKey(id), name }));
    },
    onOneOff: (id, change) => {
      const row = rows.get(id);
      if (row) void updateOneOffPaint(row, change);
    },
  };
}

/** The option's paints named with their job-price or one-off tag, for every row's paint picker. */
export const taggedPaints = (paints: readonly PaintChoice[], rates: PricingRates): PaintChoice[] => {
  const rateOf = new Map(rates.paints.map((p) => [p.id, p]));
  return paints.map((p) => {
    const rate = rateOf.get(p.key);
    return rate ? { ...p, name: paintLabel(rate) } : p;
  });
};

/** What the desk can do to the job's price book from an area's hire and other-trade rows. */
export interface JobRowActions {
  prices: JobPrice[];
  /** A job price for a hire-list item's day rate or cartage; null clears that half. */
  onHirePrice: (item: HireItemFromApi, change: { hig_dayrate?: number | null; hig_transport?: number | null }) => void;
  /** A new quoted hire item or material on the job; resolves to its row id for the area line to name. */
  onAddQuotedHire: () => Promise<string>;
  onAddMaterial: () => Promise<string>;
  /** Rename or re-price a job row; every area and option using it follows. */
  onRow: (jobPriceId: string, change: DeviceRecord['fields']) => void;
}

const NEW_QUOTED_HIRE = 'Quoted item';
const NEW_MATERIAL = 'Other trade / permit / material';

/** The extra rows' job-price actions for `measure`. */
export function jobRowActions(measure: DeviceRecord, records: readonly DeviceRecord[]): JobRowActions {
  const rows = new Map(records.filter((r) => r.table === JOB_PRICE_TABLE).map((r) => [r.id, r]));
  const addRow = async (kind: string, fields: DeviceRecord['fields']) => {
    const id = crypto.randomUUID();
    await saveNew(measure, id, kind, fields);
    return id;
  };
  return {
    prices: jobPricesOf(records, measure.id),
    onHirePrice: (item, change) => {
      const identity = { hig_name: item.name, hig_hireitemid: item.id };
      void upsertBookRow(measure, records, bookRowId('hire', measure.id, item.id), JOB_PRICE_KIND.hire, identity, change);
    },
    onAddQuotedHire: () => addRow(JOB_PRICE_KIND.hire, { hig_name: NEW_QUOTED_HIRE, hig_dayrate: 0, hig_transport: 0 }),
    onAddMaterial: () => addRow(JOB_PRICE_KIND.material, { hig_name: NEW_MATERIAL, hig_rate: 0 }),
    onRow: (id, change) => {
      const row = rows.get(id);
      if (row) void saveFields(row, change);
    },
  };
}
